import React, { useMemo } from 'react';
import { useHistory } from 'react-router';
import { Container, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { providerPatientRoute } from '@uplift/constants';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { ProviderLayout, ProviderLayoutPageHeader } from '@uplift/ui-v2';
import { ClientsStatusFilter, ClientsStatusFilterValue } from './components/ClientsStatusFilter';
import { PatientsTable } from './components/PatientsTable';
import { useAllPatientsData } from './patients-page.hooks';
import { PatientRow } from './components/PatientsTable/PatientsTable.types';

const useStyles = makeStyles()(theme => ({
  desktopContainer: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  mobileFilterButton: {
    textTransform: 'none',
  },
}));

const nameSort = ({ name: nameA }: PatientRow, { name: nameB }: PatientRow) => {
  if (nameA < nameB) {
    return -1;
  } else if (nameA > nameB) {
    return 1;
  } else {
    return 0;
  }
};

export const PatientsPage: React.VFC = () => {
  const l10n = useL10n();
  const isMobile = useIsMobile('sm');
  const { classes } = useStyles();
  const history = useHistory();
  const [ patientsKey, setPatientsKey ] = React.useState<ClientsStatusFilterValue>('active');
  const { data: patients, isLoading } = useAllPatientsData();

  const patientRows = useMemo(
    () => (patients?.[patientsKey] ?? []).sort(nameSort),
    [ patients, patientsKey ]
  );

  if (isMobile) {
    return (
      <ProviderLayout background="white">
        <ProviderLayoutPageHeader
          title={l10n.pages.clients}
          actions={(
            <ClientsStatusFilter
              classes={{ button: classes.mobileFilterButton }}
              buttonSize="small"
              patients={patients}
              value={patientsKey}
              setValue={setPatientsKey}
              disabled={patients?.all.length === 0}
            />
          )}
        />
        <Container disableGutters>
          <PatientsTable
            rows={patientRows}
            loading={isLoading}
            isMobile
            onRowClick={row => {
              history.push(providerPatientRoute.createLink({ patientId: row.id }));
            }}
          />
        </Container>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <ProviderLayoutPageHeader
        title={l10n.pages.clients}
        actions={(
          <ClientsStatusFilter
            patients={patients}
            value={patientsKey}
            setValue={setPatientsKey}
            disabled={patients?.all.length === 0}
          />
        )}
      />
      <Container className={classes.desktopContainer}>
        <Paper elevation={1}>
          <PatientsTable
            rows={patientRows}
            loading={isLoading}
          />
        </Paper>
      </Container>
    </ProviderLayout>
  );
};
